/**
 * Shared helpers for normalizing catalog identifiers.
 *
 * Canonical forms used across the sizing experience:
 * - Models: `llama3-8b`, `llama3-70b`, `mistral-large-2411`, `phi-4-mini`
 * - GPUs: `gaudi3`, `gaudi2`, `nvidia-h100`, `nvidia-a100`
 * - Servers: `xe7740`, `xe9640`
 */

const MODEL_ALIAS_ENTRIES: Array<[string, string]> = [
  ['llama-3.1-8b', 'llama3-8b'],
  ['llama-3.1-70b', 'llama3-70b'],
  ['llama 3.1 8b', 'llama3-8b'],
  ['llama 3.1 70b', 'llama3-70b'],
  ['meta-llama3-8b-instruct', 'llama3-8b'],
  ['meta-llama3-70b-instruct', 'llama3-70b'],
  ['llama3-8b-instruct', 'llama3-8b'],
  ['llama3-70b-instruct', 'llama3-70b'],
  ['mistral-large', 'mistral-large-2411'],
  ['mistral-large-instruct', 'mistral-large-2411'],
  ['phi-4-mini-instruct', 'phi-4-mini'],
];

const GPU_ALIAS_ENTRIES: Array<[string, string]> = [
  ['intel-gaudi3', 'gaudi3'],
  ['intel gaudi3', 'gaudi3'],
  ['gaudi-3', 'gaudi3'],
  ['gaudi 3', 'gaudi3'],
  ['gaudi3 pcie', 'gaudi3'],
  ['gaudi3_pcie', 'gaudi3'],
  ['intel-gaudi2', 'gaudi2'],
  ['intel gaudi2', 'gaudi2'],
  ['gaudi-2', 'gaudi2'],
  ['gaudi 2', 'gaudi2'],
  ['h100', 'nvidia-h100'],
  ['nvidia h100', 'nvidia-h100'],
  ['nvidia-h100-sxm', 'nvidia-h100'],
  ['nvidia-h100-pcie', 'nvidia-h100'],
  ['a100', 'nvidia-a100'],
  ['nvidia a100', 'nvidia-a100'],
  ['nvidia-a100-sxm', 'nvidia-a100'],
  ['nvidia-a100-pcie', 'nvidia-a100'],
];

const SERVER_ALIAS_ENTRIES: Array<[string, string]> = [
  ['xe-7740', 'xe7740'],
  ['xe 7740', 'xe7740'],
  ['dell poweredge xe7740', 'xe7740'],
  ['poweredge xe7740', 'xe7740'],
  ['xe-9640', 'xe9640'],
  ['xe 9640', 'xe9640'],
  ['dell poweredge xe9640', 'xe9640'],
  ['poweredge xe9640', 'xe9640'],
];

const MODEL_ALIASES = new Map(
  MODEL_ALIAS_ENTRIES.map(([alias, canonical]) => [alias.toLowerCase(), canonical]),
);
const GPU_ALIASES = new Map(
  GPU_ALIAS_ENTRIES.map(([alias, canonical]) => [alias.toLowerCase(), canonical]),
);
const SERVER_ALIASES = new Map(
  SERVER_ALIAS_ENTRIES.map(([alias, canonical]) => [alias.toLowerCase(), canonical]),
);

const MODEL_VENDOR_PREFIX = /^(meta|google|anthropic|openai|mistral|cohere|microsoft|amazon)-/;
const MODEL_SUFFIXES = ['-instruct', '-chat', '-hf'];

const GPU_PREFIXES = [/^intel-/, /^nvidia-/, /^aws-/, /^azure-/];
const GPU_SUFFIXES = ['-accelerator', '-gpu', '-card'];

const SERVER_PREFIXES = [/^dell poweredge /, /^hpe /, /^supermicro /];

const collapseWhitespace = (value: string) => value.replace(/\s+/g, ' ').trim();

function applyModelTransforms(raw: string): string {
  let normalized = collapseWhitespace(raw).toLowerCase();
  normalized = normalized.replace(/[_\s]+/g, '-');
  normalized = normalized.replace(MODEL_VENDOR_PREFIX, '');
  for (const suffix of MODEL_SUFFIXES) {
    if (normalized.endsWith(suffix)) {
      normalized = normalized.slice(0, -suffix.length);
    }
  }
  normalized = normalized.replace(/-+/g, '-');
  return normalized;
}

function applyGpuTransforms(raw: string): string {
  let normalized = collapseWhitespace(raw).toLowerCase();
  normalized = normalized.replace(/[_\s]+/g, '-');
  for (const prefix of GPU_PREFIXES) {
    normalized = normalized.replace(prefix, '');
  }
  for (const suffix of GPU_SUFFIXES) {
    if (normalized.endsWith(suffix)) {
      normalized = normalized.slice(0, -suffix.length);
    }
  }
  normalized = normalized.replace(/-+/g, '-');
  return normalized;
}

function applyServerTransforms(raw: string): string {
  let normalized = collapseWhitespace(raw).toLowerCase();
  for (const prefix of SERVER_PREFIXES) {
    normalized = normalized.replace(prefix, '');
  }
  normalized = normalized.replace(/[_\s-]+/g, '');
  return normalized;
}

export function normalizeModelId(input: string): string {
  if (!input) {
    return '';
  }
  const trimmed = collapseWhitespace(input).toLowerCase();
  if (!trimmed) {
    return '';
  }
  const directAlias = MODEL_ALIASES.get(trimmed);
  if (directAlias) {
    return directAlias;
  }
  const transformed = applyModelTransforms(trimmed);
  return MODEL_ALIASES.get(transformed) ?? transformed;
}

export function normalizeGpuName(input: string): string {
  if (!input) {
    return '';
  }
  const trimmed = collapseWhitespace(input).toLowerCase();
  if (!trimmed) {
    return '';
  }
  const directAlias = GPU_ALIASES.get(trimmed);
  if (directAlias) {
    return directAlias;
  }
  const transformed = applyGpuTransforms(trimmed);
  return GPU_ALIASES.get(transformed) ?? transformed;
}

export function normalizeServerName(input: string): string {
  if (!input) {
    return '';
  }
  const trimmed = collapseWhitespace(input).toLowerCase();
  if (!trimmed) {
    return '';
  }
  const directAlias = SERVER_ALIASES.get(trimmed);
  if (directAlias) {
    return directAlias;
  }
  const transformed = applyServerTransforms(trimmed);
  return SERVER_ALIASES.get(transformed) ?? transformed;
}

